import {
  collection,
  firestore,
  onSnapshot,
  query,
  where,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannelMessageReaction } from '@ps/models/lib/community-channel';

/**
 * Live snapshot of which emoji-codes the current user has reacted
 * with on a given message. Sourced from
 * `stores/{creatorId}/communityChannels/{channelId}/messages/{messageId}/reactions`
 * filtered to the current user.
 *
 * The "did I react with X?" decoration on each reaction chip uses
 * this — when the user toggles a reaction the API confirms server-
 * side and this listener flips the local highlight on/off without
 * the chip having to refetch.
 *
 * Emits the full set of emojiCodes the current user has reacted
 * with on each snapshot — callers turn that into a `Set<string>` to
 * look up "is this emojiCode mine?" in O(1).
 */
export function subscribeToMyMessageReactions(
  creatorId: string,
  channelId: string,
  messageId: string,
  userId: string,
  onChange: (emojiCodes: Set<string>) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = collection(
    db,
    'stores',
    creatorId,
    'communityChannels',
    channelId,
    'messages',
    messageId,
    'reactions',
  );
  const q = query(ref, where('userId', '==', userId));

  const unsubscribe = onSnapshot(
    q,
    snapshot => {
      const codes = new Set<string>();
      snapshot.forEach(doc => {
        const data = snapshotToDataWithId<CommunityChannelMessageReaction>(doc);
        if (data?.emojiCode) codes.add(data.emojiCode);
      });
      onChange(codes);
    },
    error => onError?.(error),
  );
  return unsubscribe;
}
